import React, { useState, useEffect } from 'react';
import { CreditCard, Save, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaybillConfigSection = ({ eventId, eventName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    paybill_number: '',
    paybill_account_prefix: ''
  });
  const [defaultConfig, setDefaultConfig] = useState({
    default_paybill: '522522',
    default_prefix: 'MZN'
  });

  useEffect(() => {
    if (eventId) {
      fetchConfig();
    }
  }, [eventId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/universal-events/events/${eventId}/paybill-config`);
      if (response.ok) {
        const data = await response.json();
        setConfig({
          paybill_number: data.paybill_number || '',
          paybill_account_prefix: data.paybill_account_prefix || ''
        });
        setDefaultConfig({
          default_paybill: data.default_paybill || '522522',
          default_prefix: data.default_prefix || 'MZN'
        });
      }
    } catch (err) {
      console.error('Error fetching paybill config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/universal-events/events/${eventId}/paybill-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Paybill configuration saved');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border p-6 animate-pulse">
        <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-100 rounded mb-3"></div>
        <div className="h-10 bg-gray-100 rounded"></div>
      </div>
    );
  }

  const isConfigured = config.paybill_number && config.paybill_number.trim() !== '';

  return (
    <div className="bg-white rounded-xl border p-6" data-testid="paybill-config-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <CreditCard className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">M-Pesa Paybill Configuration</h3>
          <p className="text-sm text-gray-500">Configure payment details for this event</p>
        </div>
        {isConfigured ? (
          <span className="ml-auto px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
            <Check className="w-4 h-4" />
            Configured
          </span>
        ) : (
          <span className="ml-auto px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Using Default
          </span>
        )}
      </div>

      <div className="space-y-4">
        {/* Paybill Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Paybill / Till Number
          </label>
          <input
            type="text"
            value={config.paybill_number}
            onChange={(e) => setConfig(prev => ({ ...prev, paybill_number: e.target.value }))}
            placeholder={`Default: ${defaultConfig.default_paybill}`}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            data-testid="paybill-number-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Your M-Pesa Business Number. Leave empty to use the default ({defaultConfig.default_paybill}).
          </p>
        </div>

        {/* Account Prefix */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Number Prefix
          </label>
          <input
            type="text"
            value={config.paybill_account_prefix}
            onChange={(e) => setConfig(prev => ({ ...prev, paybill_account_prefix: e.target.value.toUpperCase() }))}
            placeholder={`Default: ${defaultConfig.default_prefix}`}
            maxLength={10}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent uppercase"
            data-testid="paybill-prefix-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Short prefix for account numbers (e.g., "CONF" for conference, "KCC" for Karen Country Club).
            Account numbers will look like: <code className="bg-gray-100 px-1 rounded">{config.paybill_account_prefix || defaultConfig.default_prefix}ABC12345</code>
          </p>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 rounded-lg p-4 mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Payment Preview (what attendees will see):</p>
          <div className="bg-white rounded-lg p-3 border space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Paybill Number:</span>
              <span className="font-mono font-bold">{config.paybill_number || defaultConfig.default_paybill}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Number:</span>
              <span className="font-mono text-green-600">{config.paybill_account_prefix || defaultConfig.default_prefix}XXXXXXXX</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={saving} data-testid="save-paybill-config">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaybillConfigSection;
